import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import { Loader2 } from 'lucide-react';
import { ConversationState, EProjectStatus } from '@/enums';
import useTranslation from '@/hooks/useTranslation';
import PaperAirplaneIcon from '../icons/PaperAirplaneIcon';
import { Button } from '../ui/button';
import { CategoryApiResponse, InsightsSubset, Media, ProjectProps, SelectedCategory } from '@/types';
import QuestionStimuli from './QuestionStimuli';
// resolveTranslation moved to zone components
import { useGetQuestionCategories } from '@/reactQuery/get';
import { useGetQuestionMedia } from '@/reactQuery/questionMedia';
import StoryZone from './zones/StoryZone';
import ResponsesZone from './zones/ResponsesZone';
import EvidenceZone from './zones/EvidenceZone';
import DataZone from './zones/DataZone';
import ResponsesPanel from './ResponsesPanel';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';

interface QuestionItemProps {
  isPendingReRun: boolean;
  selectedQuestionInsights?: InsightsSubset;
  questionID: string;
  questionText: string;
  analysisLanguage: string;
  singleProjectData?: ProjectProps;
  newConversationsCount: number;
  onReRun: () => void;
  isRerunLoading: boolean;
  newConversationsState: ConversationState;
  projectId?: string;
  isPendingInterviewQuestions: boolean;
  questionStimuli?: Media[];
}

function QuestionItem({
  isPendingReRun,
  selectedQuestionInsights,
  questionID,
  questionText,
  analysisLanguage,
  singleProjectData,
  newConversationsCount,
  onReRun,
  isRerunLoading,
  newConversationsState,
  projectId,
  isPendingInterviewQuestions,
  questionStimuli,
}: QuestionItemProps) {
  const { lang } = useTranslation();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const rerunFilter = localStorage.getItem(`rerun_filter_${filterId}`);

  // Data fetching
  const { data: questionMedia = [], isLoading: isMediaLoading } = useGetQuestionMedia(
    projectId,
    questionID,
    filterId,
  );

  // Panel store
  const { open: openResponsesPanel } = useResponsesPanelStore();

  // Derived state
  const noEnoughInterviewData =
    selectedQuestionInsights?.summary?.length === 0 &&
    selectedQuestionInsights?.themes?.length === 0 &&
    selectedQuestionInsights?.quotes?.length === 0 &&
    selectedQuestionInsights?.opportunities?.length === 0 &&
    (!selectedQuestionInsights?.patterns_and_trends ||
      selectedQuestionInsights.patterns_and_trends.length === 0) &&
    (!selectedQuestionInsights?.voice_quotes || selectedQuestionInsights.voice_quotes.length === 0);
  const isCompletedWithInsufficientDataAndFilter =
    singleProjectData?.status === EProjectStatus.COMPLETED && noEnoughInterviewData && !!filterId;
  const isReRunInProgress = isPendingReRun || isRerunLoading;
  const dataWithStepsIsLoading = isPendingReRun || isPendingInterviewQuestions;
  const isApplyingFilter = (rerunFilter || isRerunLoading) && Boolean(filterId);
  const isRunning = (!filterId || (filterId && isRerunLoading)) && !rerunFilter;
  let messageKey = '';
  if (isApplyingFilter) {
    messageKey = 'msg.applyingFilter';
  } else if (isRunning) {
    messageKey = 'msg.running';
  }

  // Categories
  const { data: categoriesData, isPending: isPendingCategories } = useGetQuestionCategories(
    filterId,
    projectId,
    questionID,
  );
  const categories = categoriesData as CategoryApiResponse | undefined;

  const showCategoryLoadingIndicator =
    isPendingCategories && (!categories?.categories || categories.categories.length === 0);

  // Rerun filter cleanup
  useEffect(() => {
    if (
      !newConversationsState ||
      !selectedQuestionInsights ||
      noEnoughInterviewData ||
      newConversationsState === 'done'
    )
      return;
    if (rerunFilter && newConversationsState === 'idle') {
      localStorage.removeItem(`rerun_filter_${filterId}`);
    }
  }, [
    rerunFilter,
    filterId,
    newConversationsState,
    selectedQuestionInsights,
    noEnoughInterviewData,
  ]);

  // Category click → open responses panel filtered by this category.
  // The store now takes a SelectedCategory object (label + value) so
  // the multi-select in the panel header can track it directly.
  const onSelectCategoryHandler = useCallback(
    (selectedCategory: SelectedCategory) => {
      openResponsesPanel(questionID, {
        label: selectedCategory.label,
        value: selectedCategory.label,
      });
    },
    [questionID, openResponsesPanel],
  );

  // Loading/rerun state
  if ((noEnoughInterviewData || rerunFilter) && isRerunLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[50vh] text-center">
        <Button variant="default" disabled className="flex items-center gap-2 font-bold px-7">
          <Loader2 className="h-5 w-5 animate-spin" />
          <p>{lang.get(messageKey)}</p>
        </Button>
      </div>
    );
  }

  // No data with active filter — analysis should have auto-run on filter creation
  if (isCompletedWithInsufficientDataAndFilter) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[50vh] text-center gap-3">
        {isReRunInProgress ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-neutral-500">Analysis is running for this filter...</p>
          </>
        ) : (
          <>
            <p className="font-medium text-neutral-700">
              {lang.get('msg.noInterviewsThatMatchThisFilter')}
            </p>
            <p className="text-sm text-neutral-500">
              No matching interviews were found, or the analysis has not completed yet.
            </p>
          </>
        )}
      </div>
    );
  }

  // No data — show run button
  if (noEnoughInterviewData && !rerunFilter) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[65vh] text-center">
        <Button
          variant="default"
          disabled={isReRunInProgress}
          className="relative flex items-center gap-2 mt-5 font-bold px-7"
          onClick={() => onReRun()}
        >
          {isReRunInProgress ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <PaperAirplaneIcon className="size-4" />
          )}
          {isReRunInProgress ? 'Running...' : 'Run'}
          {!isRerunLoading && newConversationsCount > 0 && (
            <span className="absolute flex items-center justify-center h-5 px-1.5 text-xs text-white bg-red-600 rounded-full w-fit -top-1 -right-1.5">
              {newConversationsCount}
            </span>
          )}
        </Button>
      </div>
    );
  }

  // === MAIN CONTENT: Zone-based layout ===
  return (
    <div className="flex flex-col w-full gap-8 py-4">
      {/* Analysis in progress banner */}
      {isRerunLoading && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg text-sm text-primary">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span>Analysis is updating — results will refresh automatically.</span>
        </div>
      )}

      {/* Question Stimuli — context for what was asked */}
      {questionStimuli && questionStimuli.length > 0 && <QuestionStimuli media={questionStimuli} />}

      {/* ZONE 1: THE STORY — Summary */}
      <div id="qsection-summary" className="scroll-mt-32">
      <StoryZone
        summary={selectedQuestionInsights?.summary}
        isLoading={dataWithStepsIsLoading}
        analysisLanguage={analysisLanguage}
        voiceQuotes={selectedQuestionInsights?.voice_quotes}
        fallbackQuestionId={questionID}
      />
      </div>

      {/* ZONE 2: RESPONDENT ANSWERS — Voice of customer. Placed right
          below the summary so researchers see raw responses within the
          first viewport, before diving into AI-derived evidence/data. */}
      <div id="qsection-answers" className="scroll-mt-32">
      {questionID && (
        <ResponsesZone questionId={questionID} categoryData={categories} />
      )}
      </div>

      {/* ZONE 3: THE EVIDENCE — Findings + Quotes + Visuals */}
      <div id="qsection-evidence" className="scroll-mt-32">
      <EvidenceZone
        insights={selectedQuestionInsights}
        isLoading={dataWithStepsIsLoading}
        analysisLanguage={analysisLanguage}
        questionMedia={questionMedia}
        isMediaLoading={isMediaLoading}
        voiceQuotes={selectedQuestionInsights?.voice_quotes}
        fallbackQuestionId={questionID}
      />
      </div>

      {/* ZONE 4: THE DATA — Categories + Themes & Opportunities */}
      <div id="qsection-categories" className="scroll-mt-32">
      <DataZone
        categories={categories}
        isPendingCategories={isPendingCategories}
        insights={selectedQuestionInsights}
        isLoading={dataWithStepsIsLoading}
        analysisLanguage={analysisLanguage}
        onSelectCategory={onSelectCategoryHandler}
        projectId={projectId}
        questionID={questionID}
        questionText={questionText}
        isPendingInterviewQuestions={isPendingInterviewQuestions}
        showCategoryLoadingIndicator={showCategoryLoadingIndicator}
      />
      </div>

      {/* Responses slide-out panel. We pass categoryData so the panel
          can offer inline category editing via SingleCategoryCell /
          MultipleCategoryCell on each ResponseCard. */}
      <ResponsesPanel categoryData={categories} />
    </div>
  );
}

export default QuestionItem;
